import { useEffect, useState } from "react";
import { Phone, Loader2, Save, Pencil, UserRoundPlus } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const inputCls = "w-full rounded-lg border border-border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-phoenix-navy/30";

function Row({ label, value }) {
    return (
        <div className="flex items-center justify-between py-2 text-sm border-b border-border/40 last:border-0">
            <span className="text-muted-foreground">{label}</span>
            <span className="font-medium text-phoenix-navy text-right ml-3">{value || "—"}</span>
        </div>
    );
}

/** Lets the inmate view + keep their own emergency contact / next-of-kin current.
 * Writes the same emergency_contact_* fields staff see on the roster's "Local BP
 * next of kin" card, so an update here shows there immediately. */
export function EmergencyContactModal({ open, onOpenChange }) {
    const [contact, setContact] = useState(null);
    const [editing, setEditing] = useState(false);
    const [saving, setSaving] = useState(false);
    const [form, setForm] = useState({ emergency_contact_name: "", emergency_contact_relationship: "", emergency_contact_phone: "" });

    useEffect(() => {
        if (!open) return;
        setEditing(false);
        setContact(null);
        api.get("/inmate/profile").then((r) => {
            const inm = r.data?.inmate || {};
            const c = {
                emergency_contact_name: inm.emergency_contact_name || "",
                emergency_contact_relationship: inm.emergency_contact_relationship || "",
                emergency_contact_phone: inm.emergency_contact_phone || "",
            };
            setContact(c);
            setForm(c);
        }).catch(() => setContact({ emergency_contact_name: "", emergency_contact_relationship: "", emergency_contact_phone: "" }));
    }, [open]);

    const save = async () => {
        setSaving(true);
        try {
            await api.put("/inmate/emergency-contact", form);
            setContact(form);
            setEditing(false);
            toast.success("Emergency contact updated.");
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Could not save your emergency contact.");
        } finally {
            setSaving(false);
        }
    };

    const hasContact = !!(contact?.emergency_contact_name || contact?.emergency_contact_phone);

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-md" data-testid="emergency-contact-modal" aria-describedby={undefined}>
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <Phone className="w-5 h-5 text-phoenix-orange" /> Emergency Contact
                    </DialogTitle>
                    <p className="text-sm text-muted-foreground">Who staff should reach in an emergency. Facility staff can see this too.</p>
                </DialogHeader>

                {contact === null ? (
                    <div className="py-10 text-center text-sm text-muted-foreground">
                        <Loader2 className="w-5 h-5 inline animate-spin mr-2" /> Loading…
                    </div>
                ) : !editing ? (
                    <div className="space-y-3">
                        <div className="bg-white border border-border/60 rounded-2xl p-4">
                            <Row label="Name" value={contact.emergency_contact_name} />
                            <Row label="Relationship" value={contact.emergency_contact_relationship} />
                            <Row label="Phone" value={contact.emergency_contact_phone} />
                        </div>
                        <button data-testid="emergency-contact-edit-btn" onClick={() => setEditing(true)}
                            className="w-full inline-flex items-center justify-center gap-1.5 px-4 py-2.5 rounded-xl bg-phoenix-navy text-white text-sm font-semibold hover:bg-phoenix-navy-2 transition-all">
                            {hasContact ? <Pencil className="w-4 h-4" /> : <UserRoundPlus className="w-4 h-4" />}
                            {hasContact ? "Update emergency contact" : "Add emergency contact"}
                        </button>
                    </div>
                ) : (
                    <div className="space-y-3" data-testid="emergency-contact-edit-form">
                        <div>
                            <label className="mb-1 block text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Name</label>
                            <input data-testid="emergency-contact-name-input" className={inputCls} value={form.emergency_contact_name}
                                onChange={(e) => setForm((f) => ({ ...f, emergency_contact_name: e.target.value }))} />
                        </div>
                        <div>
                            <label className="mb-1 block text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Relationship</label>
                            <input data-testid="emergency-contact-relationship-input" className={inputCls} value={form.emergency_contact_relationship}
                                onChange={(e) => setForm((f) => ({ ...f, emergency_contact_relationship: e.target.value }))} />
                        </div>
                        <div>
                            <label className="mb-1 block text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Phone</label>
                            <input data-testid="emergency-contact-phone-input" className={inputCls} value={form.emergency_contact_phone}
                                onChange={(e) => setForm((f) => ({ ...f, emergency_contact_phone: e.target.value }))} />
                        </div>
                        <div className="flex items-center justify-end gap-2 pt-1">
                            <button data-testid="emergency-contact-cancel-btn" onClick={() => { setForm(contact); setEditing(false); }}
                                className="rounded-xl border border-border px-4 py-2 text-sm font-semibold text-muted-foreground hover:bg-secondary/40">Cancel</button>
                            <button data-testid="emergency-contact-save-btn" onClick={save} disabled={saving}
                                className="inline-flex items-center gap-1.5 rounded-xl bg-phoenix-navy px-4 py-2 text-sm font-semibold text-white hover:bg-phoenix-navy-2 disabled:opacity-60">
                                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Save
                            </button>
                        </div>
                    </div>
                )}
            </DialogContent>
        </Dialog>
    );
}
